import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const money = (n) => Number(n || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const keyOf = (i) => `${i.participant}|${i.category}`;
const POOL_KEY = "__pool__";
// Lines that can fund the issue without going negative (used by "flag a new agreement").
const roomLines = (i) => [
  ...i.other_lines.map((o) => ({ key: o.category, label: o.category, remaining: o.available })),
  ...(i.pool_available != null ? [{ key: POOL_KEY, label: "Unscheduled hours pool", remaining: i.pool_available }] : []),
];

// Shown when saving a shift would take a support category past what the participant's service
// agreement allows (or the category isn't in the agreement). The roster manager chooses where the
// funding comes from; the choices are sent back with the save.
export function BudgetCheckDialog({ dlg, canOverride, onCancel, onConfirm }) {
  const [choice, setChoice] = useState({});
  useEffect(() => { setChoice({}); }, [dlg]);
  if (!dlg) return null;
  const set = (i, patch) => setChoice((c) => ({ ...c, [keyOf(i)]: { ...(c[keyOf(i)] || {}), ...patch } }));
  const ready = (i) => {
    const c = choice[keyOf(i)];
    if (!c?.action) return false;
    if (c.action === "other_line") return !!c.to_category;
    if (c.action === "override") return !!c.to_category && !!(c.reason || "").trim();
    if (c.action === "new_sa") return !!c.to_category && (roomLines(i).length > 0 || !!(c.reason || "").trim());
    return true;
  };
  const allReady = dlg.issues.every(ready);
  const confirm = () => onConfirm(dlg.issues.map((i) => {
    const { action, to_category, reason } = choice[keyOf(i)];
    return { participant: i.participant, category: i.category, action, ...(action === "pool" ? {} : { to_category }), ...(action === "override" || (action === "new_sa" && !roomLines(i).length) ? { reason: (reason || "").trim() } : {}) };
  }));

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="budget-check-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Check the service agreement budget</DialogTitle>
          <DialogDescription>This shift can't be covered by the participant's service agreement as it stands. Choose where the funding should come from.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {dlg.issues.map((i) => {
            const c = choice[keyOf(i)] || {};
            return (
              <div key={keyOf(i)} className="rounded-lg border border-slate-200 p-3" data-testid={`budget-issue-${i.category}`}>
                <div className="text-sm font-semibold text-slate-800">{i.participant_name} · {i.category}</div>
                <div className="text-xs text-slate-500 mb-2">
                  {i.kind === "missing"
                    ? <>This category isn't in the service agreement. The shift{i.amount ? ` (${money(i.amount)})` : ""} needs funding from somewhere else.</>
                    : <>{money(i.amount)} is more than the {money(i.available)} left on this line of the agreement.</>}
                </div>
                <div className="space-y-1.5 text-sm">
                  {i.other_lines.length > 0 && (
                    <label className="flex items-start gap-2">
                      <input type="radio" name={keyOf(i)} checked={c.action === "other_line"} onChange={() => set(i, { action: "other_line", to_category: c.to_category || i.other_lines[0].category })} className="mt-1" />
                      <span className="flex-1">Draw from another line
                        {c.action === "other_line" && (
                          <select value={c.to_category || ""} onChange={(e) => set(i, { to_category: e.target.value })} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm" data-testid="budget-other-line">
                            {i.other_lines.map((o) => <option key={o.category} value={o.category}>{o.category} ({money(o.available)} left)</option>)}
                          </select>)}
                      </span>
                    </label>)}
                  {i.pool_available != null && (
                    <label className="flex items-start gap-2">
                      <input type="radio" name={keyOf(i)} checked={c.action === "pool"} onChange={() => set(i, { action: "pool" })} className="mt-1" />
                      <span>Draw from the unscheduled hours pool ({money(i.pool_available)} available)</span>
                    </label>)}
                  {(() => {
                    const room = roomLines(i);
                    const noRoomAllowed = room.length === 0 && canOverride && i.override_targets?.length > 0;
                    const options = room.length ? room : (noRoomAllowed ? i.override_targets : []);
                    const blocked = options.length === 0;
                    const target = options.find((t) => t.key === c.to_category);
                    const after = target ? target.remaining - i.amount : null;
                    return (
                      <label className={`flex items-start gap-2 ${blocked ? "opacity-60" : ""}`}>
                        <input type="radio" name={keyOf(i)} disabled={blocked} checked={c.action === "new_sa"} onChange={() => set(i, { action: "new_sa", to_category: c.to_category || options[0].key })} className="mt-1" data-testid="budget-flag-radio" />
                        <span className="flex-1">Flag that a new service agreement is needed, and fund this shift from a line meanwhile
                          {blocked && <span className="block text-xs text-slate-500">No line has enough left. A Rostering Manager has to flag this one.</span>}
                          {c.action === "new_sa" && !blocked && (
                            <>
                              <select value={c.to_category || ""} onChange={(e) => set(i, { to_category: e.target.value })} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm" data-testid="budget-flag-line">
                                {options.map((t) => <option key={t.key} value={t.key}>{t.label} ({money(t.remaining)} left)</option>)}
                              </select>
                              {noRoomAllowed && (
                                <>
                                  {after != null && after < 0 && <span className="block text-xs text-rose-600 mt-1">No line has enough left. This takes {target.label} {money(-after)} over its allocation.</span>}
                                  <Textarea value={c.reason || ""} onChange={(e) => set(i, { reason: e.target.value })} placeholder="Reason" className="mt-1 text-sm min-h-[56px]" data-testid="budget-flag-reason" />
                                </>)}
                            </>)}
                        </span>
                      </label>);
                  })()}
                  {canOverride && i.override_targets?.length > 0 && (() => {
                    const own = i.override_targets.find((t) => t.key === i.category) || i.override_targets[0];
                    const target = i.override_targets.find((t) => t.key === c.to_category);
                    const after = target ? target.remaining - i.amount : null;
                    return (
                      <label className="flex items-start gap-2">
                        <input type="radio" name={keyOf(i)} checked={c.action === "override"} onChange={() => set(i, { action: "override", to_category: c.to_category || own.key })} className="mt-1" />
                        <span className="flex-1">Override: draw from a line that doesn't have enough (Rostering Manager)
                          {c.action === "override" && (
                            <>
                              <select value={c.to_category || ""} onChange={(e) => set(i, { to_category: e.target.value })} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm" data-testid="budget-override-line">
                                {i.override_targets.map((t) => <option key={t.key} value={t.key}>{t.label} ({money(t.remaining)} left)</option>)}
                              </select>
                              {after != null && after < 0 && <span className="block text-xs text-rose-600 mt-1" data-testid="budget-override-negative">This takes {target.label} {money(-after)} over its allocation.</span>}
                              <Textarea value={c.reason || ""} onChange={(e) => set(i, { reason: e.target.value })} placeholder="Reason for the override" className="mt-1 text-sm min-h-[56px]" data-testid="budget-override-reason" />
                            </>)}
                        </span>
                      </label>);
                  })()}
                </div>
              </div>);
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Back to shift</Button>
          <Button onClick={confirm} disabled={!allReady} style={{ backgroundColor: "#bf3b4f" }} data-testid="budget-check-confirm">Save shift</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
